import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/use-current-user';
import { accountSettingsService, type FeatureMode } from '@/services/account-settings-service';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  RadioGroup,
  RadioGroupItem,
  toast,
} from 'prosci-ui';
import { Settings, Copy } from '@/lib/icons';

const KAIYA_MCP_URL = import.meta.env.VITE_KAIYA_MCP_URL || '';

export function KaiyaMcpSettingsTab() {
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const queryClient = useQueryClient();
  const accountId = currentUser?.organization_id;

  const [pendingDisable, setPendingDisable] = useState(false);

  const { data: accountSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['account-settings', accountId],
    queryFn: () => accountSettingsService.getSettings(accountId),
    enabled: !!accountId && !userLoading,
  });

  const kaiyaMcpMode: FeatureMode = accountSettings?.kaiya_mcp_mode ?? 'per_user';

  const setFeatureModeMutation = useMutation({
    mutationFn: async (mode: FeatureMode) => {
      await accountSettingsService.setFeatureMode(accountId, 'kaiya_mcp', mode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', accountId] });
      queryClient.invalidateQueries({ queryKey: ['accountSettings', accountId] });
      toast.success('Kaiya MCP setting updated successfully.');
    },
    onError: () => {
      toast.error('Failed to update Kaiya MCP setting. Please try again.');
    },
  });

  const handleModeChange = (mode: FeatureMode) => {
    if (mode === 'disabled') {
      setPendingDisable(true);
      return;
    }
    setFeatureModeMutation.mutate(mode);
  };

  const handleConfirmDisable = () => {
    setPendingDisable(false);
    setFeatureModeMutation.mutate('disabled');
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(KAIYA_MCP_URL);
      toast.success('MCP server URL copied to clipboard');
    } catch {
      toast.error('Failed to copy MCP server URL');
    }
  };

  if (userLoading || settingsLoading || !accountId) {
    return null;
  }

  return (
    <div className="space-y-6">
      {KAIYA_MCP_URL && (
        <Card>
          <CardHeader>
            <CardTitle>MCP Server</CardTitle>
            <CardDescription>
              Users connect their chat assistants (Claude, ChatGPT, Microsoft Copilot, and others) to Kaiya with this server URL.
              Each user signs in with their own Prosci credentials — no shared API keys.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <code className="bg-gray-100 px-3 py-2 rounded text-sm break-all flex-1">
                {KAIYA_MCP_URL}
              </code>
              <Button variant="outline" size="sm" onClick={handleCopyUrl}>
                <Copy className="h-4 w-4" />
                Copy
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Settings className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-xl">Kaiya MCP Settings</CardTitle>
              <CardDescription className="mt-1.5">
                Manage which users in your organization can connect their chat assistants to Kaiya
                through the Model Context Protocol (MCP).
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="divide-y divide-gray-200">
            <div className="flex flex-col gap-2 py-4 first:pt-0 sm:flex-row sm:items-start sm:gap-4">
              <div className="flex items-start gap-3 sm:flex-1">
                <div className="mt-0.5 shrink-0 w-14" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <Label className="text-base font-medium">Kaiya MCP Access</Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Allow users to sign in to Kaiya from their own chat assistants. Access is authenticated
                    per user with their Prosci login.
                  </p>
                  <div className="mt-4 mb-2">
                    <span className="text-sm font-medium">Who can connect:</span>
                  </div>
                  <RadioGroup
                    value={kaiyaMcpMode}
                    onValueChange={(value: string) => handleModeChange(value as FeatureMode)}
                    disabled={setFeatureModeMutation.isPending}
                    className="max-w-md"
                  >
                    <label
                      htmlFor="kaiya-mcp-all"
                      className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer has-checked:border-primary has-checked:ring-1 has-checked:ring-primary"
                    >
                      <RadioGroupItem value="all_users" id="kaiya-mcp-all" className="mt-0.5" />
                      <div>
                        <span className="text-sm font-medium">Enabled for All Users</span>
                        <p className="text-sm text-muted-foreground">
                          Every user in your organization can connect their chat assistants to Kaiya.
                        </p>
                      </div>
                    </label>
                    <label
                      htmlFor="kaiya-mcp-disabled"
                      className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer has-checked:border-primary has-checked:ring-1 has-checked:ring-primary"
                    >
                      <RadioGroupItem value="disabled" id="kaiya-mcp-disabled" className="mt-0.5" />
                      <div>
                        <span className="text-sm font-medium">Disabled for All Users</span>
                        <p className="text-sm text-muted-foreground">
                          No users can connect chat assistants to Kaiya.
                        </p>
                      </div>
                    </label>
                    <label
                      htmlFor="kaiya-mcp-per-user"
                      className="flex items-start gap-3 rounded-lg border p-4 cursor-pointer has-checked:border-primary has-checked:ring-1 has-checked:ring-primary"
                    >
                      <RadioGroupItem value="per_user" id="kaiya-mcp-per-user" className="mt-0.5" />
                      <div>
                        <span className="text-sm font-medium">Configured per User</span>
                        <p className="text-sm text-muted-foreground">
                          Manage MCP access by user in Users → Edit Users → Apps → Kaiya.
                        </p>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={pendingDisable} onOpenChange={() => setPendingDisable(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Kaiya MCP</DialogTitle>
            <DialogDescription>
              Clicking Confirm will disable Kaiya MCP access for your organization. Users with
              connected chat assistants will lose access the next time their session is checked.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDisable(false)}
              disabled={setFeatureModeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDisable}
              disabled={setFeatureModeMutation.isPending}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
